import { buildApiUrl, servicePost } from "./client.ts";
import { batchedServicePost } from "./readBatch.ts";
import type { ImportFixAvailability } from "./importFix.ts";

export interface UploadMetadata {
  purpose?: "excel-import" | "media" | "product-image" | "packagedeal-image" | "";
  context?: Record<string, unknown>;
}

export interface UploadResult {
  StoredPath: string;
  ProcessingResult: string | null;
  ProcessingError: string | null;
  /** A failed excel-import only: whether the assistant can fix the stored file (ImportFix.vb). */
  AssistantFix?: ImportFixAvailability | null;
}

interface UploadResponse {
  bError: boolean;
  sMessage: string;
  oData: UploadResult;
}

/**
 * A failed upload, with what the server said about it. `message` is the server's own text when it
 * sent one ("File exceeds 20MB limit"), not just the status. `result` is set when the server got as
 * far as storing the file and then failed to process it - an import that failed, for example - so
 * the caller still knows where the file is (`result.StoredPath`).
 */
export class UploadError extends Error {
  readonly status: number;
  readonly result: UploadResult | null;

  constructor(message: string, status: number, result: UploadResult | null) {
    super(message);
    this.name = "UploadError";
    this.status = status;
    this.result = result;
  }
}

function parseUploadResponse(text: string): UploadResponse | null {
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed && typeof parsed === "object" ? (parsed as UploadResponse) : null;
  } catch {
    return null;
  }
}

/**
 * Upload a file to /api/v2/userfile/upload with optional purpose-specific processing.
 * Uses XHR for upload progress tracking. `onUploadComplete` fires once bytes
 * have left the client — server then runs purpose-specific SP work, which can
 * take longer than the upload itself for large imports. Rejects with an
 * {@link UploadError}.
 */
export function uploadFile(
  file: File,
  authHeaders: Record<string, string>,
  metadata?: UploadMetadata,
  onProgress?: (percent: number) => void,
  onUploadComplete?: () => void,
): Promise<UploadResult> {
  const formData = new FormData();
  formData.append("file", file);
  if (metadata) {
    formData.append("metadata", JSON.stringify(metadata));
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", buildApiUrl("/api/v2/userfile/upload"));
    for (const [k, v] of Object.entries(authHeaders)) xhr.setRequestHeader(k, v);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    if (onUploadComplete) {
      xhr.upload.onload = () => onUploadComplete();
    }

    xhr.onload = () => {
      // The server answers refusals (400, 401) in the same JSON envelope as a processed upload.
      const response = parseUploadResponse(xhr.responseText);
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new UploadError(response?.sMessage || `Upload failed: ${xhr.status}`, xhr.status, response?.oData ?? null));
        return;
      }
      if (!response) {
        reject(new UploadError("Invalid server response", xhr.status, null));
        return;
      }
      if (response.bError) {
        reject(new UploadError(response.sMessage || "Upload failed", xhr.status, response.oData ?? null));
        return;
      }
      resolve(response.oData);
    };

    xhr.onerror = () => reject(new UploadError("Upload network error", 0, null));
    xhr.send(formData);
  });
}

export interface UserExportInfo {
  ExportId: string;
  OriginalFilename: string;
  /** Sidecar purpose — "scheduled" for worker-run exports, "assistant" for files an assistant
   *  answer offered; absent/empty for interactive exports. */
  Purpose?: string | null;
  Report: string | null;
  ReportDisplayName: string | null;
  FileType: string | null;
  UploadedAt: string;
  SizeBytes: number;
  OriginalSizeBytes?: number;
  Month: string;
  Name: string;
  Filters: string | null;
  OrderBy: string | null;
  Result: string | null;
  Error: string | null;
  /** The conversation an assistant file came from (Purpose "assistant"). */
  ConversationGuid?: string | null;
}

export async function getOwnFiles(maxResults = 50): Promise<UserExportInfo[]> {
  const result = await batchedServicePost<UserExportInfo[]>("WebGetOwnFiles", { maxResults });
  if (result.bError) throw new Error(result.sMessage || "Failed loading files");
  return result.oData ?? [];
}

export interface UserStorageQuota {
  UsedBytes: number;
  LimitBytes: number;
  ExportBytes: number;
  UploadBytes: number;
  FileCount: number;
  OldestExportAt?: string | null;
}

export async function getOwnStorageQuota(): Promise<UserStorageQuota> {
  const result = await batchedServicePost<UserStorageQuota>("WebGetOwnStorageQuota", {});
  if (result.bError) throw new Error(result.sMessage || "Failed loading quota");
  return result.oData;
}

export async function deleteOwnExport(exportId: string): Promise<boolean> {
  const result = await servicePost<boolean>("WebDeleteOwnExport", { exportId });
  if (result.bError) throw new Error(result.sMessage || "Delete failed");
  return result.oData === true;
}

export async function deleteAllOwnExports(): Promise<number> {
  const result = await servicePost<number>("WebDeleteAllOwnExports", {});
  if (result.bError) throw new Error(result.sMessage || "Delete all failed");
  return result.oData ?? 0;
}

/** Download an exported file belonging to the current user (month+name only). */
export async function downloadOwnExport(month: string, name: string, authHeaders: Record<string, string>): Promise<void> {
  const url = buildApiUrl(`/api/v2/userfile/download?month=${encodeURIComponent(month)}&name=${encodeURIComponent(name)}`);
  await fetchAndTriggerDownload(url, name, authHeaders);
}

/**
 * Fetches an owner's UserStore item via /api/v2/userfile/preview and returns an
 * in-browser object URL suitable for <img src>. Caller MUST revoke with
 * URL.revokeObjectURL when done to avoid leaks. Server gates access via the
 * sidecar AllowPreview flag — only purposes that stored previewable bytes
 * (product-image, packagedeal-image, media) resolve; others return 404.
 *
 * @param storedPath user-relative path returned by uploadFile, e.g. "2026-04/<guid>_name.gif"
 */
export async function fetchUserStorePreview(storedPath: string, authHeaders: Record<string, string>): Promise<string> {
  const slash = storedPath.indexOf("/");
  if (slash <= 0) throw new Error(`Invalid storedPath: ${storedPath}`);
  const month = storedPath.slice(0, slash);
  const name = storedPath.slice(slash + 1);
  const url = buildApiUrl(`/api/v2/userfile/preview?month=${encodeURIComponent(month)}&name=${encodeURIComponent(name)}`);
  const response = await fetch(url, { method: "GET", headers: authHeaders });
  if (!response.ok) {
    throw new Error(`Preview ${response.status}: ${response.statusText}`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Download an export by the `DownloadPath` the server handed back (an id-based
 * `/api/v2/userfile/download?exportId=…` link). Used by the statement detail-sheet download, which
 * gets a FINISHED export back in one call rather than polling for one.
 *
 * NOTE: `exportDialog.tsx` carries its own byte-identical copy of this (`triggerAuthDownload`),
 * which predates it. Left alone rather than refactored in the middle of another change; worth
 * collapsing into this one.
 */
export async function downloadExportByPath(
  path: string,
  fallbackName: string,
  authHeaders: Record<string, string>,
): Promise<void> {
  const url = path.startsWith("/") ? buildApiUrl(path) : path;
  await fetchAndTriggerDownload(url, fallbackName, authHeaders);
}

/**
 * SuperAdmin download: view any user's exported or uploaded file.
 */
export async function downloadUserFile(
  month: string,
  name: string,
  userId: number,
  scope: "export" | "upload",
  authHeaders: Record<string, string>,
): Promise<void> {
  const url = buildApiUrl(`/api/v2/userfile/download?month=${encodeURIComponent(month)}&name=${encodeURIComponent(name)}&userId=${userId}&scope=${scope}`);
  await fetchAndTriggerDownload(url, name, authHeaders);
}

/** Extract the download filename from a Content-Disposition header. Handles all three
 *  server emissions: RFC 5987 `filename*=UTF-8''…` (preferred), quoted `filename="…"`,
 *  and bare-token `filename=report.csv` (what ContentDispositionHeaderValue emits for
 *  plain ASCII names). Returns null when the header carries no filename. */
export function filenameFromContentDisposition(disposition: string): string | null {
  const star = disposition.match(/filename\*\s*=\s*utf-8''([^;]+)/i);
  if (star?.[1]) {
    try { return decodeURIComponent(star[1].trim()); } catch { /* fall through */ }
  }
  const quoted = disposition.match(/filename\s*=\s*"([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const bare = disposition.match(/filename\s*=\s*([^;\s]+)/i);
  return bare?.[1] ?? null;
}

async function fetchAndTriggerDownload(url: string, fallbackName: string, authHeaders: Record<string, string>): Promise<void> {
  const response = await fetch(url, { method: "GET", headers: authHeaders });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`Download ${response.status}: ${body || response.statusText}`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = filenameFromContentDisposition(disposition) ?? fallbackName;

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ── Unchanged helpers ──

export interface MediaType {
  id: number;
  value: string;
  description: string;
  sFileVerification: string;
}

export async function getMediaTypes(): Promise<MediaType[]> {
  const result = await servicePost<Array<{
    iKioskMediaTypeID: number;
    sName: string;
    sDescription: string;
    sFileVerification: string;
  }>>("GetMediaTypes", {});
  if (result.bError) throw new Error(result.sMessage || "Failed loading media types");

  return (result.oData ?? [])
    .filter((item) => ![30, 31, 32].includes(item.iKioskMediaTypeID))
    .map((item) => ({
      id: item.iKioskMediaTypeID,
      value: item.sName,
      description: item.sDescription,
      sFileVerification: item.sFileVerification,
    }));
}

export async function deleteMedia(mediaID: number): Promise<boolean> {
  const result = await servicePost<boolean>("DeleteMedia", { mediaID });
  if (result.bError) throw new Error(result.sMessage || "Failed deleting media");
  return result.oData;
}
